import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { limsClient } from '../api/limsClient';
import type { LimsUser } from '@mariana/labos-client';
import { Breadcrumb } from './Breadcrumb';
import { formatDate } from '../utils/formatDate';
import './AdminUsersPage.css';

export function AdminUsersPage() {
  const auth = useAuth();
  const [users, setUsers] = useState<LimsUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingEmail, setTogglingEmail] = useState<string | null>(null);

  useEffect(() => {
    limsClient.listUsers().then(data => {
      setUsers(data);
      setLoading(false);
    }).catch(err => {
      setError(err instanceof Error ? err.message : 'Failed to load users');
      setLoading(false);
    });
  }, []);

  const handleAdminToggle = async (email: string, isAdmin: boolean) => {
    setTogglingEmail(email);
    setError(null);
    try {
      await limsClient.setUserAdmin(email, isAdmin);
      setUsers(prev => prev.map(u => u.email === email ? { ...u, is_admin: isAdmin } : u));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update admin status');
    } finally {
      setTogglingEmail(null);
    }
  };

  if (!auth?.isAdmin) {
    return (
      <div className="entity-view">
        <div className="entity-view-header">
          <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }]} />
        </div>
        <div className="table-empty">Access denied. <a href="/dashboard">Back to dashboard</a></div>
      </div>
    );
  }

  return (
    <div className="entity-view">
      <div className="entity-view-header">
        <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: 'Users' }]} />
      </div>

      {error && (
        <div className="error-message">Error: {error}</div>
      )}

      <div className="entity-table-container">
        {loading ? (
          <div className="table-loading">
            <div className="loading-spinner" />
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="entity-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Admin</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>{user.name ?? <span className="null-value">—</span>}</td>
                    <td>
                      <label className="admin-toggle">
                        <input
                          type="checkbox"
                          checked={user.is_admin}
                          disabled={togglingEmail === user.email}
                          onChange={e => handleAdminToggle(user.email, e.target.checked)}
                        />
                        <span className="admin-toggle-slider" />
                      </label>
                    </td>
                    <td>{user.last_login_at ? formatDate(user.last_login_at) : <span className="null-value">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
